import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";

import { storage } from "integrations/firebase/storage";

/**
 * Uploaded pictures that belong to one content document: a partner's logo, a
 * speaker's photo.
 *
 * Each lives at `events/{eventId}/{collection}/{entityId}/{random}`, which is
 * the shape `storage.rules` matches for both collections. Three things follow
 * from that path:
 *
 *  - The entity id is in the path, so the backend cleanup that deletes a
 *    replaced picture (functions/python/app/application/service/
 *    entity_image_cleanup.py) can tell this record's uploads from a URL copied
 *    in from elsewhere, such as the roster avatar a linked speaker starts
 *    with, and never deletes the latter. The browser deletes only a file it
 *    uploaded for a save that then failed.
 *  - Every upload gets a new name. Overwriting a fixed name rotates the
 *    download token, which kills the URL on the document before the new one
 *    is saved; a new name leaves the old picture working until the document
 *    stops pointing at it.
 *  - Event deletion removes everything under `events/{eventId}/`, so these go
 *    with the event.
 */

/** What `isImage()` in storage.rules accepts. SVG is left out on purpose: it
 *  can carry script, and the bucket serves it from a Google domain. */
export const ENTITY_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];

const EXTENSIONS: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

export function entityImageFolder(
  eventId: string,
  collection: string,
  entityId: string,
): string {
  return `events/${eventId}/${collection}/${entityId}/`;
}

/** The sentence to show for a file the rules would refuse, or null. */
export function entityImageProblem(
  file: File,
  maxBytes: number,
): string | null {
  if (!ENTITY_IMAGE_TYPES.includes(file.type))
    return "Choose a PNG, JPEG or WebP image.";
  if (file.size > maxBytes)
    return `That image is larger than ${Math.round(maxBytes / (1024 * 1024))} MB.`;
  return null;
}

function randomName(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function uploadEntityImage(
  eventId: string,
  collection: string,
  entityId: string,
  file: File,
): Promise<{ path: string; url: string }> {
  const path = `${entityImageFolder(eventId, collection, entityId)}${randomName()}.${EXTENSIONS[file.type] ?? "img"}`;
  const target = ref(storage, path);
  await uploadBytes(target, file, { contentType: file.type });
  return { path, url: await getDownloadURL(target) };
}

function isObjectMissing(cause: unknown): boolean {
  return (
    typeof cause === "object" &&
    cause !== null &&
    "code" in cause &&
    (cause as { code?: unknown }).code === "storage/object-not-found"
  );
}

/** True when the object is gone afterwards, including when it already was.
 *  For discarding an upload whose save failed; nothing else calls it. */
export async function deleteEntityImage(path: string): Promise<boolean> {
  try {
    await deleteObject(ref(storage, path));
    return true;
  } catch (cause) {
    return isObjectMissing(cause);
  }
}
